/* The Atelier product lightbox (snippets/product-media-modal.liquid, styles in assets/at-lightbox.css).
   Dawn's <product-modal> opens and closes it (focus trap, Escape, scroll lock, focus return); product-modal.js
   calls show(index) with the opener's photo. One horizontal scroll-snap track serves every viewport: phones swipe
   it natively, desktop steps it instantly with the arrows, thumbnails and keys. One passive scroll listener keeps
   the counter, thumbnails, arrows and status line in step. A click or tap on a photo toggles a 2x zoom that keeps
   the point under the pointer; the zoomed slide pans by scrolling. The Zoom button beside Close (aria-pressed) does
   the same around the photo's centre for the keyboard and screen readers, and while zoomed the arrow keys pan the
   photo instead of stepping or unzooming (Home, End and the button unzoom). No libraries, timers or observers. */
if (!customElements.get('at-lightbox')) {
  customElements.define(
    'at-lightbox',
    class AtLightbox extends HTMLElement {
      connectedCallback() {
        if (this.track) return; // <product-modal> moves itself to <body>, which connects this element twice
        this.track = this.querySelector('.at-lb-track');
        this.slides = Array.from(this.track.children);
        this.thumbs = Array.from(this.querySelectorAll('.at-lb-thumbs button'));
        this.prev = this.querySelector('.at-lb-prev');
        this.next = this.querySelector('.at-lb-next');
        this.zoomBtn = this.querySelector('.at-lb-zoom');
        this.index = 0;
        this.track.addEventListener('scroll', () => this.update(Math.round(this.track.scrollLeft / this.track.clientWidth)), { passive: true });
        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('keydown', this.onKey.bind(this));
      }

      // Images arrive without src/srcset (see product-media.liquid) so a closed dialog downloads nothing, even in
      // Safari; the first open fills them in and loading="lazy" keeps it to the photos near the one shown. A photo the
      // gallery has already loaded shows that very file at once (its currentSrc, from memory), so opening never shows
      // an empty box; sharpen() then swaps in the lightbox's own candidate once it has decoded (a larger file on
      // computers, where the lightbox photo is wider than the gallery's; the same URL on phones).
      hydrate() {
        if (this.hydrated) return;
        this.hydrated = true;
        this.slides.forEach((slide) => {
          const img = slide.querySelector('img[data-src]');
          const twin = img && document.querySelector(`media-gallery li[data-media-id$="-${slide.dataset.mediaId}"] .product__media img`);
          if (twin && twin.complete && twin.naturalWidth && twin.currentSrc) {
            // This file is already visible in the gallery. Async decoding lets the new img paint an empty tile first.
            img.decoding = 'sync';
            img.src = twin.currentSrc;
          } else if (img) this.fill(img);
        });
        this.querySelectorAll('img[data-src]:not([src])').forEach((img) => this.fill(img));
      }

      fill(img) {
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
        img.removeAttribute('data-srcset');
      }

      // A slide still showing the gallery's copy gets its own candidates: decoded off screen with the same sizes
      // first, then that decoded image takes the old one's place. Assigning its srcset to the old img could still
      // paint an empty frame while that element selected its source. If the sharper file fails, the gallery's copy stays
      // and so do the candidates, so the next showing of the slide tries again (swapping anyway broke the photo).
      sharpen(i) {
        const img = this.slides[i] && this.slides[i].querySelector('img[data-src][src]');
        if (!img || img.atSharpening) return;
        img.atSharpening = true;
        const probe = new Image();
        probe.sizes = img.sizes;
        if (img.dataset.srcset) probe.srcset = img.dataset.srcset;
        probe.src = img.dataset.src;
        probe.decode().then(
          () => {
            if (probe.sizes !== img.sizes) {
              img.atSharpening = false;
              return this.sharpen(i); // zoom changed the requested size while this candidate decoded
            }
            Array.from(img.attributes).forEach(({ name, value }) => {
              if (!['src', 'srcset', 'sizes', 'data-src', 'data-srcset'].includes(name)) probe.setAttribute(name, value);
            });
            img.replaceWith(probe);
          },
          () => (img.atSharpening = false)
        );
      }

      show(i) {
        i = Math.max(0, Math.min(this.slides.length - 1, i));
        const firstOpen = !this.hydrated;
        const img0 = this.slides[i] && this.slides[i].querySelector('img[data-src]');
        if (img0) img0.loading = 'eager'; // set before the URLs go in, so the shown photo starts at once
        this.hydrate();
        this.unzoom();
        const img = this.slides[i].querySelector('img');
        if (img) img.loading = 'eager'; // start the shown photo now rather than on the lazy-load pass
        // Even a cached src is assigned asynchronously. Keep the gallery visible until this first copy can paint;
        // the dialog still takes focus immediately. A failed image must not leave an invisible modal over the page.
        if (firstOpen && img && img.dataset.src && !img.complete) {
          const modal = this.closest('product-modal');
          modal.style.opacity = '0';
          const reveal = () => modal.style.removeProperty('opacity');
          img.decode().then(reveal, reveal);
        }
        this.track.scrollTo({ left: i * this.track.clientWidth, behavior: 'instant' });
        this.update(i, true);
      }

      update(i, force) {
        if ((i === this.index && !force) || !this.slides[i]) return;
        if (this.slides[this.index].querySelector('[loaded]')) window.pauseAllMedia();
        this.index = i;
        this.querySelector('[data-at-lb-index]').textContent = i + 1;
        this.thumbs.forEach((thumb, k) => (k === i ? thumb.setAttribute('aria-current', 'true') : thumb.removeAttribute('aria-current')));
        if (this.thumbs[i]) {
          this.thumbs[i].scrollIntoView({ block: 'nearest', inline: 'nearest' });
          this.querySelector('[data-at-lb-status]').textContent = this.thumbs[i].getAttribute('aria-label');
        }
        if (this.prev) {
          this.prev.setAttribute('aria-disabled', i === 0);
          this.next.setAttribute('aria-disabled', i === this.slides.length - 1);
        }
        if (this.zoomBtn) this.zoomBtn.setAttribute('aria-disabled', !this.slides[i].classList.contains('at-lb-slide--image'));
        [i, i + 1, i - 1].forEach((k) => this.sharpen(k));
      }

      onClick(event) {
        const target = event.target;
        const arrow = target.closest('.at-lb-arrow');
        const thumb = target.closest('.at-lb-thumbs button');
        const photo = target.closest('.at-lb-slide--image .at-lb-img');
        if (target.closest('.at-lb-zoom')) {
          const img = this.slides[this.index].querySelector('.at-lb-slide--image .at-lb-img');
          if (this.zoomBtn.getAttribute('aria-disabled') !== 'true' && img && !this.unzoom()) {
            const r = img.getBoundingClientRect();
            this.zoom(img, { clientX: r.left + r.width / 2, clientY: r.top + r.height / 2 });
          }
        } else if (arrow) {
          if (arrow.getAttribute('aria-disabled') !== 'true') this.show(this.index + (arrow === this.next ? 1 : -1));
        } else if (thumb) {
          this.show(this.thumbs.indexOf(thumb));
        } else if (photo) {
          this.zoom(photo, event);
        } else if ((target === this.track || target.classList.contains('at-lb-slide')) && window.matchMedia('(min-width: 801px)').matches) {
          this.closest('product-modal').hide(); // desktop: a click on the paper around the photo closes
        }
      }

      onKey(event) {
        const zoomed = this.track.querySelector('.at-lb-slide.is-zoomed');
        const pan = zoomed && { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[event.key];
        if (pan) {
          // Zoomed: the arrows move around the photo, a fifth of the screen at a time.
          event.preventDefault();
          zoomed.scrollBy(pan[0] * zoomed.clientWidth * 0.2, pan[1] * zoomed.clientHeight * 0.2);
          return;
        }
        const last = this.slides.length - 1;
        const to = { ArrowLeft: this.index - 1, ArrowRight: this.index + 1, Home: 0, End: last }[event.key];
        if (to === undefined || last < 1 || event.target.closest('video, iframe, model-viewer')) return;
        event.preventDefault();
        if (!this.unzoom()) this.show(to); // a zoomed photo unzooms first
      }

      zoom(img, event) {
        if (this.unzoom()) return;
        const slide = img.closest('.at-lb-slide');
        const before = img.getBoundingClientRect();
        const fx = (event.clientX - before.left) / before.width;
        const fy = (event.clientY - before.top) / before.height;
        this.resize(img, Math.round(before.width * 2) + 'px'); // a sharper candidate for the zoomed photo
        this.sharpen(this.slides.indexOf(slide));
        slide.classList.add('is-zoomed');
        this.track.classList.add('is-zoomed');
        if (this.zoomBtn) this.zoomBtn.setAttribute('aria-pressed', 'true');
        const after = img.getBoundingClientRect(); // keep the clicked point under the pointer
        slide.scrollLeft += after.left + fx * after.width - event.clientX;
        slide.scrollTop += after.top + fy * after.height - event.clientY;
      }

      // Gives the slide's img a new sizes without a blank frame. Set straight on the img, the browser dropped the
      // photo it was showing while it fetched and decoded the larger file (one or two paper-coloured frames as the
      // zoom began). Now that file is decoded off screen first (the sharpen() way) and the img is told its new size
      // only then, when it picks the cached, decoded file. A slide still showing the gallery's copy (no srcset yet)
      // takes the sizes at once: sharpen() then fetches for it the same way. If the larger file fails, the photo keeps
      // its size and file (the zoom still works, from the file it has) and the next zoom tries again.
      resize(img, sizes) {
        if (!img.srcset || img.sizes === sizes) {
          img.sizes = sizes;
          return;
        }
        const probe = new Image();
        probe.sizes = sizes;
        probe.srcset = img.srcset;
        probe.src = img.src;
        probe.decode().then(
          () => (img.sizes = sizes),
          () => {}
        );
      }

      unzoom() {
        const slide = this.track.querySelector('.at-lb-slide.is-zoomed');
        if (!slide) return false;
        slide.classList.remove('is-zoomed');
        this.track.classList.remove('is-zoomed');
        if (this.zoomBtn) this.zoomBtn.setAttribute('aria-pressed', 'false');
        return true;
      }
    }
  );
}
